import React from "react";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Image from "@insightfulscience/atomic-react/Image";
import {
	PostElementLine,
	PostElementListItem,
	PostElementParagraph,
	PostElementUl,
} from "@insightfulscience/atomic-react/PostElement";

import Link from "@insightfulscience/atomic-react/Link";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroBackground from "@insightfulscience/atomic-react/Hero/HeroBackground";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import Feature from "@insightfulscience/atomic-react/Feature";
import LandingSimpleSection from "@insightfulscience/atomic-react/r2/LandingElement/SimpleSection";
import LandingSimpleSectionStripped from "@insightfulscience/atomic-react/LandingElement/SimpleSectionStripped";
import Box from "@insightfulscience/atomic-react/Box";
import { useLocale } from "@insightfulscience/smart-react/i18";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import UList from "@insightfulscience/atomic-react/UList";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "../../SiteLayout";
import InvolveYourTeam from "../../InvolveYourTeam";
import WithServices from "../../services";
import SectionStart30DayTrial from "../../SectionStart30DayTrial";
import withErrorHandling from "../../AppError/withErrorHandling";

const CompanyPage = () => {
	const [t] = useLocale("survival-analysis");
	const siteDomain = process.env.MAIN_SITE_DOMAIN;

	return (
		<SiteLayout>
			<PageMeta title={t("META_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("META_TITLE")}
					description={t("META_DESCRIPTION")}
					url={`https://www.${siteDomain}/features/prism-survival-analysis`}
					image="/images/graphpad/logo/logo-graphpad-dotmatics.png"
					isImageBaseUrl
				/>
				<PageMeta.Canonical href="https://www.graphpad.com/features/prism-survival-analysis" />
			</PageMeta>

			<Hero color="gradient" align="left">
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
				<HeroDescription>{t("HERO_SUBTITLE")}</HeroDescription>
				<HeroAction center={false}>
					<HeroRow center={false}>
						<HeroCol shrink>
							<HeroButtonLink href="/demos/">{t("HERO_START_A_FREE_TRIAL")}</HeroButtonLink>
						</HeroCol>
						<HeroCol shrink>
							<HeroButtonLink href="/how-to-buy/" isOutline2 styleType="info-light">
								{t("HERO_PRICING_BUTTON")}
							</HeroButtonLink>
						</HeroCol>
					</HeroRow>
				</HeroAction>
				<HeroBackground>
					<Image
						src="/images/graphpad/survival-analysis/graphic-step-graph-576.png"
						className="h-60"
					/>
				</HeroBackground>
			</Hero>

			<LandingSimpleSectionStripped>
				<Row mx my utility={{ justify: "center" }}>
					<Col sm={12} md={6}>
						<PostElementParagraph>{t("LEFT_BULLET_ELEMENT_1")}</PostElementParagraph>
					</Col>
					<Col sm={12} md={6}>
						<Box utility={{ bgColor: "black--02" }} spacing={{ py: 4, px: 8 }}>
							<PostElementParagraph>{t("RIGHT_BULLET_ELEMENT_1")}</PostElementParagraph>
							<PostElementUl>
								<PostElementListItem>{t("RIGHT_BULLET_ELEMENT_2")}</PostElementListItem>
								<PostElementListItem>{t("RIGHT_BULLET_ELEMENT_3")}</PostElementListItem>
								<PostElementListItem>{t("RIGHT_BULLET_ELEMENT_4")}</PostElementListItem>
							</PostElementUl>
						</Box>
					</Col>
				</Row>
			</LandingSimpleSectionStripped>

			<LandingSimpleSectionStripped isDark>
				<LandingSimpleSection.TitleV2>{t("SURVIVAL_ANALYSES_TITLE")}</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.DescriptionV2>
					{t("SURVIVAL_ANALYSES_SUBTITLE")}
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Row mx my utility={{ justify: "center" }}>
						<Col sm={12} md={6}>
							<Box utility={{ bgColor: "white" }} shadow>
								<Image src="/images/graphpad/survival-analysis/kaplan-meier.gif" />
							</Box>
							<Paragraph sx={{ fontSize: "xxxl" }}>{t("WISTIA_1_SUBTEXT_1")}</Paragraph>
							<Paragraph>{t("WISTIA_1_SUBTEXT_2")}</Paragraph>
						</Col>
						<Col sm={12} md={6}>
							<Box utility={{ bgColor: "white" }} shadow>
								<Image src="/images/graphpad/survival-analysis/cox-regression.gif" />
							</Box>
							<Paragraph sx={{ fontSize: "xxxl" }}>{t("WISTIA_2_SUBTEXT_1")}</Paragraph>
							<Paragraph>{t("WISTIA_2_SUBTEXT_2")}</Paragraph>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>

			<LandingSimpleSectionStripped>
				<LandingSimpleSection.TitleV2>{t("PERFORMING_SIMPLE_TITLE")}</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.DescriptionV2>
					{t("PERFORMING_SIMPLE_SUBTITLE")}
				</LandingSimpleSection.DescriptionV2>
				<LandingSimpleSection.Body>
					<Feature top>
						<FeatureMeta numberItem="1">
							<FeatureMeta.Title>{t("STEPPER_TITLE_1")}</FeatureMeta.Title>
							<FeatureMeta.Description>{t("STEPPER_DESCRIPTION_1")}</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage
							src="/images/graphpad/survival-analysis/survival-in-Prism-Step-1-w800.jpg"
							shadow
						/>
					</Feature>

					<Feature top>
						<FeatureMeta numberItem="2">
							<FeatureMeta.Title>{t("STEPPER_TITLE_2")}</FeatureMeta.Title>
							<FeatureMeta.Description>{t("STEPPER_DESCRIPTION_2")}</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage
							src="/images/graphpad/survival-analysis/survival-in-Prism-Step-2-w800.jpg"
							shadow
						/>
					</Feature>

					<Feature top>
						<FeatureMeta numberItem="3">
							<FeatureMeta.Title>{t("STEPPER_TITLE_3")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("STEPPER_DESCRIPTION_3")}</Paragraph>
								<Paragraph>{t("STEPPER_DESCRIPTION_3_2")}</Paragraph>
								<UList>
									<UList.Item>{t("STEPPER_DESCRIPTION_3_3")}</UList.Item>
									<UList.Item>{t("STEPPER_DESCRIPTION_3_4")}</UList.Item>
									<UList.Item>{t("STEPPER_DESCRIPTION_3_5")}</UList.Item>
								</UList>
							</FeatureMeta.Description>
						</FeatureMeta>
						<FeatureImage
							src="/images/graphpad/survival-analysis/survival-in-Prism-Step-3-w800.jpg"
							shadow
						/>
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>

			<InvolveYourTeam />

			<LandingSimpleSectionStripped>
				<PostElementLine />
				<LandingSimpleSection.TitleV2>
					{t("ACCESS_OTHER_SURVIVAL_TITLE")}
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Row mx my utility={{ justify: "center" }} spacing={{ my: 10 }}>
						<Col sm={12} md={4}>
							<Link href="https://go.graphpad.com/video/understanding-kaplan-meier">
								<Image
									className="w-100"
									src="/images/graphpad/anova/survival-kaplan-meier-w1146-800.png"
								/>
							</Link>
						</Col>
						<Col sm={12} md={4}>
							<Link href="https://www.graphpad.com/guides/survival-analysis">
								<Image
									className="w-100"
									src="/images/graphpad/anova/survival-prism-survival-w1146-800.png"
								/>
							</Link>
						</Col>
						<Col sm={12} md={4}>
							<Link href="https://go.graphpad.com/video/cox-proportional-hazards-regression-analysis">
								<Image
									className="w-100"
									src="/images/graphpad/anova/survival-cox-proportional-w1146-800.png"
								/>
							</Link>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<SectionStart30DayTrial />
		</SiteLayout>
	);
};

export default pipe(CompanyPage, withErrorHandling, WithServices);
